// JSON 読み書き・ディープマージ（i18n パッチ用）・ツリー走査の共通関数
import { readFileSync, writeFileSync, existsSync, mkdirSync } from 'node:fs';
import path from 'node:path';

export function readJson(absPath) {
    return JSON.parse(readFileSync(absPath, 'utf-8'));
}

export function readJsonIfExists(absPath) {
    return existsSync(absPath) ? readJson(absPath) : null;
}

export function writeJson(absPath, value) {
    mkdirSync(path.dirname(absPath), { recursive: true });
    writeFileSync(absPath, JSON.stringify(value, null, 2) + '\n', 'utf-8');
}

export function isPlainObject(v) {
    return v !== null && typeof v === 'object' && !Array.isArray(v);
}

// パッチは LLM 出力・外部貼り付け由来のため、プロトタイプ汚染につながるキーは常に無視する
const UNSAFE_MERGE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * i18n パッチ用ディープマージ（キー単位の追記・上書き。配列と非オブジェクトは置換）。
 * F1 --sample / F11 / F12 の i18n スタブ反映で共用する。
 * 返り値: 上書き/追加したリーフキー数。
 */
export function deepMergeInto(target, patch, ignoreKeys = ['_comment']) {
    let count = 0;
    for (const [key, value] of Object.entries(patch)) {
        if (UNSAFE_MERGE_KEYS.has(key)) continue;
        if (ignoreKeys.includes(key)) continue;
        if (isPlainObject(value) && isPlainObject(target[key])) {
            count += deepMergeInto(target[key], value, ignoreKeys);
        } else {
            target[key] = value;
            count += 1;
        }
    }
    return count;
}

/** ドットパス（"startMenu.title"）でネストされた値を取得。 */
export function getByDotPath(obj, dotPath) {
    let cur = obj;
    for (const seg of dotPath.split('.')) {
        if (!isPlainObject(cur) || !(seg in cur)) return undefined;
        cur = cur[seg];
    }
    return cur;
}

/** ドットパスで値を設定（中間オブジェクトは作成）。 */
export function setByDotPath(obj, dotPath, value) {
    const segs = dotPath.split('.');
    let cur = obj;
    for (const seg of segs.slice(0, -1)) {
        if (!isPlainObject(cur[seg])) cur[seg] = {};
        cur = cur[seg];
    }
    cur[segs.at(-1)] = value;
}

/** ドットパスのキーを削除。削除したら true。 */
export function deleteByDotPath(obj, dotPath) {
    const segs = dotPath.split('.');
    let cur = obj;
    for (const seg of segs.slice(0, -1)) {
        if (!isPlainObject(cur[seg])) return false;
        cur = cur[seg];
    }
    if (isPlainObject(cur) && segs.at(-1) in cur) {
        delete cur[segs.at(-1)];
        return true;
    }
    return false;
}

/**
 * JSON ツリーを走査し、各ノードで visitor({ key, value, path, parent }) を呼ぶ。
 * path は "areaEvents[0].actions[1].mapId" 形式。
 */
export function walkJson(value, visitor, jsonPath = '') {
    if (Array.isArray(value)) {
        value.forEach((item, i) => walkJson(item, visitor, `${jsonPath}[${i}]`));
    } else if (isPlainObject(value)) {
        for (const [key, child] of Object.entries(value)) {
            const childPath = jsonPath ? `${jsonPath}.${key}` : key;
            visitor({ key, value: child, path: childPath, parent: value });
            walkJson(child, visitor, childPath);
        }
    }
}

/** 指定キー名の値を全収集: [{ path, value }] */
export function collectByKey(root, keyName) {
    const found = [];
    walkJson(root, ({ key, value, path: p }) => {
        if (key === keyName) found.push({ path: p, value });
    });
    return found;
}

/** ネストされたオブジェクトのリーフキーをドットパスで列挙（配列はリーフ扱い）。 */
export function leafPaths(obj, prefix = '') {
    const paths = [];
    for (const [key, value] of Object.entries(obj)) {
        const p = prefix ? `${prefix}.${key}` : key;
        if (isPlainObject(value)) {
            paths.push(...leafPaths(value, p));
        } else {
            paths.push(p);
        }
    }
    return paths;
}
